import {
  property,
  state,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';
import { DeesThumbnailBase } from '../dees-thumbnail-shared/DeesThumbnailBase.js';
import { thumbnailBaseStyles } from '../dees-thumbnail-shared/styles.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-thumbnail-video': DeesThumbnailVideo;
  }
}

@customElement('dees-thumbnail-video')
export class DeesThumbnailVideo extends DeesThumbnailBase {
  public static demo = demo;
  public static demoGroups = ['Media'];
  public static styles = [
    ...thumbnailBaseStyles,
    css`
      .video-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: var(--dees-color-bg-secondary);
      }

      .video-wrapper video {
        width: 100%;
        height: 100%;
        object-fit: var(--thumbnail-fit, cover);
        display: block;
      }

      .video-wrapper canvas {
        width: 100%;
        height: 100%;
        object-fit: var(--thumbnail-fit, cover);
        display: block;
      }

      .poster-image {
        width: 100%;
        height: 100%;
        object-fit: var(--thumbnail-fit, cover);
        display: block;
      }


      .play-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 15;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }

      .play-overlay dees-icon {
        font-size: 16px;
        color: white;
      }


      .video-hover-preview {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: var(--thumbnail-fit, cover);
        z-index: 5;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .video-hover-preview.active {
        opacity: 1;
      }
    `,
  ] as any;

  @property({ type: String })
  accessor fit: 'cover' | 'contain' = 'cover';

  @property({ type: String })
  accessor src: string = '';

  @property({ type: String })
  accessor poster: string = '';

  @state()
  accessor duration: number = 0;

  @state()
  accessor thumbnailCaptured: boolean = false;

  @state()
  accessor isHovering: boolean = false;

  private thumbnailCanvas: HTMLCanvasElement | null = null;
  private hoverVideo: HTMLVideoElement | null = null;
  private hasStartedLoading: boolean = false;
  private loadController?: AbortController;

  protected renderTileContent(): TemplateResult {
    return html`
      <div class="video-wrapper" style=${`--thumbnail-fit:${this.fit}`}>
        ${this.poster ? html`
          <img class="poster-image" src="${this.poster}" alt="" @error=${() => { this.error = true; }} />
        ` : this.thumbnailCaptured ? html`
          <canvas></canvas>
        ` : html`
          <div style="width: 100%; height: 100%; background: var(--dees-color-bg-secondary);"></div>
        `}

        ${this.isHovering && this.src ? html`
          <video
            class="video-hover-preview ${this.isHovering ? 'active' : ''}"
            .src=${this.src}
            muted
            playsinline
            @loadeddata=${this.handleHoverVideoLoaded}
          ></video>
        ` : ''}
      </div>

      ${!this.isHovering ? html`
        <div class="play-overlay">
          <dees-icon icon="lucide:Play"></dees-icon>
        </div>
      ` : ''}


    `;
  }

  protected renderBottomBar(): TemplateResult | string {
    if (!this.label && !this.duration) return '';
    return html`
      <div class="tile-info-bar">
        ${this.label ? html`<span class="info-label" title="${this.label}">${this.label}</span>` : ''}
        <span class="info-spacer"></span>
        ${this.duration > 0 ? html`<span class="info-detail">${this.formatTime(this.duration)}</span>` : ''}
      </div>
    `;
  }

  protected getTileClickDetail(): Record<string, unknown> {
    return {
      src: this.src,
      poster: this.poster,
      duration: this.duration,
    };
  }

  protected onBecameVisible(): void {
    if (!this.hasStartedLoading && this.src) {
      this.hasStartedLoading = true;
      this.captureFirstFrame();
    }
  }

  private async captureFirstFrame(): Promise<void> {
    this.loadController?.abort();
    const controller = new AbortController();
    this.loadController = controller;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = this.poster ? 'metadata' : 'auto';
    this.loading = !this.poster;
    this.error = false;
    try {
      await new Promise<void>((resolve, reject) => {
        controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
        video.addEventListener('error', () => reject(new Error('Video unavailable')), { once: true, signal: controller.signal });
        video.addEventListener(this.poster ? 'loadedmetadata' : 'loadeddata', () => resolve(), { once: true, signal: controller.signal });
        video.src = this.src;
      });
      if (controller.signal.aborted || !this.isConnected) return;
      this.duration = video.duration;
      if (!this.poster) {
        const canvas = document.createElement('canvas');
        const preview = this.shadowRoot!.querySelector<HTMLElement>('.tile-preview')!;
        const width = Math.max(preview.clientWidth, preview.clientHeight * video.videoWidth / video.videoHeight) * (window.devicePixelRatio || 1);
        canvas.width = Math.max(1, Math.min(video.videoWidth, width));
        canvas.height = Math.max(1, Math.round(canvas.width * video.videoHeight / video.videoWidth));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Video preview context unavailable');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        this.thumbnailCanvas = canvas;
        this.thumbnailCaptured = true;
      }
    } catch {
      if (!controller.signal.aborted) this.error = true;
    } finally {
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (this.loadController === controller) {
        this.loadController = undefined;
        this.loading = false;
      }
    }
  }

  private copyThumbnailToCanvas(): void {
    if (!this.thumbnailCanvas) return;
    const canvas = this.shadowRoot?.querySelector('.video-wrapper canvas') as HTMLCanvasElement;
    if (!canvas) return;

    canvas.width = this.thumbnailCanvas.width;
    canvas.height = this.thumbnailCanvas.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(this.thumbnailCanvas, 0, 0);
    }
  }

  protected onTileMouseEnter(): void {
    if (this.clickable && !this.loading && !this.error && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.isHovering = true;
  }

  protected onTileMouseLeave(): void {
    this.isHovering = false;
    this.stopHoverVideo();
  }

  private handleHoverVideoLoaded(e: Event): void {
    if (!this.isHovering || !this.isConnected) return;
    this.hoverVideo = e.target as HTMLVideoElement;
    this.hoverVideo.play().catch(() => {
      // Autoplay may be blocked
    });
  }

  private stopHoverVideo(): void {
    const video = this.hoverVideo || this.shadowRoot?.querySelector<HTMLVideoElement>('.video-hover-preview');
    if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
    this.hoverVideo = null;
  }

  protected willUpdate(changed: Map<PropertyKey, unknown>): void {
    super.willUpdate(changed);
    if (changed.has('src') || changed.has('poster') || changed.has('size')) {
      this.loadController?.abort();
      this.loadController = undefined;
      this.stopHoverVideo();
      this.isHovering = false;
      this.hasStartedLoading = false;
      this.thumbnailCaptured = false;
      this.thumbnailCanvas = null;
      this.duration = 0;
      this.loading = false;
      this.error = false;
      if (this.src && this.isVisible) this.onBecameVisible();
    }
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);
    if (this.thumbnailCaptured) this.copyThumbnailToCanvas();
  }

  public async disconnectedCallback(): Promise<void> {
    this.loadController?.abort();
    this.loadController = undefined;
    this.stopHoverVideo();
    this.isHovering = false;
    this.hasStartedLoading = false;
    this.thumbnailCanvas = null;
    this.thumbnailCaptured = false;
    this.loading = false;
    await super.disconnectedCallback();
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
